import {
    HiChevronDoubleLeft,
    HiChevronDoubleRight,
    HiChevronLeft,
    HiChevronRight,
    HiOutlineStar,
    HiStar,
} from 'react-icons/hi2'
import {useQuery, useSuspenseQuery} from '@tanstack/react-query'
import {memo, Suspense, useState} from 'react'
import {useTranslations} from 'use-intl'
import clsx from 'clsx'
import {ProductComment} from '~/client/spreadshop/old/fetchProductReviews'
import ResponsiveDialog from '~/components/common/ResponsiveDialog'
import {productTypeReviewsQueryOptions} from '~/client/fn/reviews'
import useLocaleParams from '~/hooks/useLocaleParams'
import {Product, ProductType} from '~/gql/graphql'
import Card from '~/components/common/Card'

type DialogProps = {
    onClose: () => void
    product: Product
    open: boolean
}

const ReviewsDialog = memo(({onClose, product, open}: DialogProps) => {
    const t = useTranslations('productDetails.reviewsDialog')
    const productType = product.type ?? undefined

    return (
        <ResponsiveDialog
            open={open}
            onOpenChange={onClose}
            title={t('title')}
            description={t('description', {product: productType?.name})}
            ssr
        >
            {!productType && (
                <Card className="mx-4 md:mx-0" variant="desructive">
                    {t('notFound', {productId: product.id})}
                </Card>
            )}
            {!!productType && (
                <Suspense fallback={<div>{t('loading')}</div>}>
                    <Reviews productType={productType} />
                </Suspense>
            )}
        </ResponsiveDialog>
    )
})

type ReviewsProps = {
    productType: ProductType
}

const Reviews = ({productType}: ReviewsProps) => {
    const params = useLocaleParams()
    const t = useTranslations('productDetails.reviewsDialog.reviews')
    const [page, setPage] = useState<number>(1)
    const {data: firstData} = useSuspenseQuery(
        productTypeReviewsQueryOptions(productType.id!, 1, undefined, undefined, params.locale)
    )

    const {data: latestData, isLoading} = useQuery(
        productTypeReviewsQueryOptions(productType.id!, page, undefined, undefined, params.locale)
    )

    const data = latestData ?? firstData

    return (
        <div className="flex flex-col overflow-hidden pb-8 md:p-0">
            <div className="mx-4 flex flex-row gap-2 mb-1 items-baseline md:mx-0">
                <h2 className="font-bold truncate">{t('title', {product: productType.name})}</h2>
                <span className="text-gray-400 text-sm text-nowrap">
                    ({t('count', {count: data.ratingsTotalCount})})
                </span>
            </div>
            <div className="mx-4 text-gray-400 text-xs mb-2 md:mx-0">{t('hint')}</div>

            {data.comments.length === 0 && (
                <div className="p-8 gap-4 flex flex-col items-center justify-center min-h-[50svh]">
                    <RatingBadge rating={0} className="opacity-45" />
                    <div className="font-medium">{t('empty')}</div>
                </div>
            )}

            {data.comments.length > 0 && (
                <>
                    <ul
                        className={clsx(
                            'columns-1 md:columns-2 gap-4 space-y-4 px-4 overflow-y-auto md:px-0',
                            isLoading && 'animate-pulse'
                        )}
                    >
                        {data.comments.map((comment, index) => (
                            <li
                                key={index}
                                className="break-inside-avoid-column first:pt-4 last:pb-4 md:last:p-0 md:first:p-0"
                            >
                                <ReviewCard {...comment} />
                            </li>
                        ))}
                    </ul>

                    <div className="flex flex-row gap-4 pt-4 px-4 items-center justify-center md:px-0">
                        <button
                            className="disabled:opacity-45"
                            disabled={!data.hasPreviousPage || isLoading}
                            onClick={() => setPage(1)}
                        >
                            <HiChevronDoubleLeft className="size-6" />
                            <span className="sr-only">{t('goToFirstPage')}</span>
                        </button>
                        <button
                            className="disabled:opacity-45"
                            disabled={!data.hasPreviousPage || isLoading}
                            onClick={() => setPage((page) => page - 1)}
                        >
                            <HiChevronLeft className="size-6" />
                            <span className="sr-only">{t('goToPreviousPage')}</span>
                        </button>
                        <div>{t('pageOf', {page, limit: data.pageLimit})}</div>
                        <button
                            className="disabled:opacity-45"
                            disabled={!data.hasNextPage || isLoading}
                            onClick={() => setPage((page) => page + 1)}
                        >
                            <HiChevronRight className="size-6" />
                            <span className="sr-only">{t('goToNextPage')}</span>
                        </button>
                        <button
                            className="disabled:opacity-45"
                            disabled={!data.hasNextPage || isLoading}
                            onClick={() => setPage(data.pageLimit)}
                        >
                            <HiChevronDoubleRight className="size-6" />
                            <span className="sr-only">{t('goToLastPage')}</span>
                        </button>
                    </div>
                </>
            )}
        </div>
    )
}

const ReviewCard = ({rating, comment, submitted}: ProductComment) => {
    return (
        <div className="px-4 py-2 border space-y-2 border-white rounded-md shadow-gray-600 shadow bg-gray-100/10 backdrop-blur-sm transform-gpu">
            <div className="flex flex-row gap-2 items-center">
                <RatingBadge rating={Number(rating)} />
                <span className="rounded-full size-1 bg-gray-400" aria-hidden="true" />
                <div className="text-gray-300 text-sm">{submitted}</div>
            </div>
            <p className="leading-5">{comment}</p>
        </div>
    )
}

type RatingBadgeProps = {
    className?: string
    rating: number
}

const RatingBadge = memo(({rating, className}: RatingBadgeProps) => {
    const t = useTranslations('productDetails.reviewsDialog.reviews')

    return (
        <ul
            className={clsx('flex flex-row gap-1', className)}
            aria-label={t('ratingLabel', {stars: rating})}
        >
            {[...new Array(5)].map((_, index) => (
                <li key={index}>
                    {index + 1 <= rating ? (
                        <HiStar className="fill-green-600 text-green-600 size-5" />
                    ) : (
                        <HiOutlineStar className="text-gray-300 size-5" />
                    )}
                </li>
            ))}
        </ul>
    )
})

export default ReviewsDialog
